// Standard Yu-Gi-Oh deck interchange, two interoperable formats:
//   • .ydk text  — line-based, `#main` / `#extra` / `!side` section markers, one passcode per line.
//   • ydke:// URL — the compact clipboard form: three base64 blobs of little-endian uint32
//     passcodes, separated by "!". Paste-to-share.
// Card ids in this game ARE the Konami passcodes (see packages/yo2-data decode-cards.ts), so a deck
// round-trips through either format with no mapping.

export interface YdkDeck {
  main: number[];
  extra: number[];
  side: number[];
}

/** Parse a .ydk text body. Lines before the first marker fall into Main (some exporters omit it). */
export function parseYdk(text: string): YdkDeck {
  const out: YdkDeck = { main: [], extra: [], side: [] };
  let section: keyof YdkDeck = "main";
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    if (line === "#main") { section = "main"; continue; }
    if (line === "#extra") { section = "extra"; continue; }
    if (line === "!side") { section = "side"; continue; }
    if (line[0] === "#" || line[0] === "!") continue; // "#created by …" and any other marker
    const id = Number(line);
    if (Number.isInteger(id) && id > 0) out[section].push(id);
  }
  return out;
}

/** Serialize to a .ydk text body (trailing newline). */
export function toYdk(deck: YdkDeck, creator = "YGO Evolution"): string {
  return [
    `#created by ${creator}`,
    "#main",
    ...deck.main.map(String),
    "#extra",
    ...deck.extra.map(String),
    "!side",
    ...deck.side.map(String),
  ].join("\n") + "\n";
}

// ── ydke:// (base64 of packed little-endian uint32 passcodes) ──────────────────
function b64ToIds(b64: string): number[] {
  if (!b64) return [];
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const view = new DataView(bytes.buffer);
  const out: number[] = [];
  for (let i = 0; i + 4 <= bytes.length; i += 4) out.push(view.getUint32(i, true));
  return out;
}
function idsToB64(ids: number[]): string {
  const bytes = new Uint8Array(ids.length * 4);
  const view = new DataView(bytes.buffer);
  ids.forEach((id, i) => view.setUint32(i * 4, id >>> 0, true));
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

/** Parse an `ydke://<main>!<extra>!<side>!` URL. */
export function parseYdke(url: string): YdkDeck {
  const [m = "", e = "", s = ""] = url.slice("ydke://".length).split("!");
  return { main: b64ToIds(m), extra: b64ToIds(e), side: b64ToIds(s) };
}

/** Serialize to an `ydke://` URL (the trailing "!" is part of the spec). */
export function toYdke(deck: YdkDeck): string {
  return `ydke://${idsToB64(deck.main)}!${idsToB64(deck.extra)}!${idsToB64(deck.side)}!`;
}

/** Auto-detect: an `ydke://` URL or a .ydk text body. */
export function parseDeck(input: string): YdkDeck {
  const t = input.trim();
  return t.startsWith("ydke://") ? parseYdke(t) : parseYdk(input);
}
